import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "@/components/AuthLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/LanguageContext";

import termsManifest from "@/legal/terms-manifest.json";

export default function Register() {
  const { t, lang } = useI18n();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [isAdult, setIsAdult] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  useEffect(() => { setTermsAccepted(false); }, [lang]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isAdult) {
      setError(t("auth.errors.mustBeAdult"));
      return;
    }
    if (!termsAccepted) {
      setError(lang === "it" ? "Accetta i Termini di servizio per registrarti." : "Accept the Terms of Service to register.");
      return;
    }
    if (password.length < 8) {
      setError(t("auth.errors.minPassword"));
      return;
    }
    setLoading(true);
    const res = await register({
      email,
      password,
      is_adult: true,
      terms_accepted: termsAccepted,
      terms_version: termsManifest.version,
      language: lang,
      country: country || null,
      city: city || null,
    });
    setLoading(false);
    if (res.ok) {
      navigate("/app/discover", { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <AuthLayout title={t("auth.register.title")} subtitle={t("auth.register.subtitle")} testId="register-page">
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
        <div>
          <label htmlFor="register-email-input" className="text-xs uppercase tracking-widest" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("common.email")}
          </label>
          <input id="register-email-input"
            type="email"
            required
            className="nvl-input mt-2"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="register-email-input"
            autoComplete="email"
          />
        </div>
        <div>
          <label htmlFor="register-password-input" className="text-xs uppercase tracking-widest" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("common.password")}
          </label>
          <input id="register-password-input"
            type="password"
            required
            minLength={8}
            className="nvl-input mt-2"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="register-password-input"
            autoComplete="new-password"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="register-country-input" className="text-xs uppercase tracking-widest" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("auth.register.country")}
            </label>
            <input id="register-country-input"
              type="text"
              className="nvl-input mt-2"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              data-testid="register-country-input"
              autoComplete="country-name"
            />
          </div>
          <div>
            <label htmlFor="register-city-input" className="text-xs uppercase tracking-widest" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("auth.register.city")}
            </label>
            <input id="register-city-input"
              type="text"
              className="nvl-input mt-2"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              data-testid="register-city-input"
              autoComplete="address-level2"
            />
          </div>
        </div>

        <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="register-adult-label">
          <input
            type="checkbox"
            checked={isAdult}
            onChange={(e) => setIsAdult(e.target.checked)}
            className="mt-1 h-4 w-4"
            data-testid="register-adult-checkbox"
            required
          />
          <span>{t("auth.register.adult")}</span>
        </label>

        <div className="flex items-start gap-3 text-sm">
          <input id="accept-terms" type="checkbox" required checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 shrink-0" data-testid="register-terms-checkbox" />
          <div>
            <label htmlFor="accept-terms" className="cursor-pointer">
              {termsManifest.languages[lang].acceptance_text}
            </label>{" "}
            <a href={`/legal/terms?lang=${lang}`} target="_blank" rel="noreferrer" className="underline"
              data-testid="register-legal-terms">{lang === "it" ? "Leggi i termini" : "Read the terms"}</a>
          </div>
        </div>
        <p className="text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {lang === "it" ? "Per sapere come trattiamo i tuoi dati, leggi l’" : "To learn how we process your data, read our "}
          <a href={`/legal/privacy?lang=${lang}`} target="_blank" rel="noreferrer" className="underline"
            data-testid="register-legal-privacy">{t("app.legal.privacyTitle")}</a>.
        </p>

        {error ? (
          <div className="text-sm" style={{ color: "hsl(var(--destructive))" }} data-testid="register-error">
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={loading}
          className="nvl-btn nvl-btn-primary w-full disabled:opacity-60"
          data-testid="register-submit-button"
        >
          {loading ? t("common.loading") : t("auth.register.submit")}
        </button>

        <div className="text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {t("auth.register.have")}{" "}
          <Link to="/login" data-testid="register-login-link" className="font-medium underline">
            {t("auth.register.login")}
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
